import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { isNotificationSupported, getNotificationStatus, requestNotificationPermission, sendNotification, getIOSInstallHint, registerServiceWorker, subscribeToPush, unsubscribeFromPush, sendTestPush } from '../utils/notifications';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, ReferenceLine, ReferenceArea, ReferenceDot, Cell, ComposedChart, Line, Area
} from 'recharts';
import { Clock, TrendingUp, TrendingDown, Minus, RefreshCw, BarChart3, Table, Activity, ChevronDown, Bell, BellOff } from 'lucide-react';
import FreshnessDot from './FreshnessDot';
import PriceRangeBar from './PriceRangeBar';

import { BACKEND_URL } from "../config/api";

const API_URL = BACKEND_URL;

// Timeframe options for historical data
const TIMEFRAME_OPTIONS = [
  { value: 2, label: '2D' },
  { value: 5, label: '5D' },
  { value: 10, label: '10D' },
  { value: 20, label: '20D' },
  { value: 30, label: '30D' },
  { value: 60, label: '60D' },
];

// Correlation window options (in minutes)
const CORR_WINDOW_OPTIONS = [
  { value: 1, label: '5m', intervals: 1 },   // 1 x 5-min = 5 min
  { value: 2, label: '10m', intervals: 2 },   // 2 x 5-min = 10 min
  { value: 3, label: '15m', intervals: 3 },   // 3 x 5-min = 15 min
  { value: 6, label: '30m', intervals: 6 },   // 6 x 5-min = 30 min
];

export default function IntradayPatternsChart({ ticker, date }) {
  const [data, setData] = useState(null);
  const [todayData, setTodayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('chart'); // Default to 'chart'
  const [correlation, setCorrelation] = useState(null);
  const [selectedDays, setSelectedDays] = useState(2); // Default to 2 days
  const [correlations30Min, setCorrelations30Min] = useState([]); // window correlations
  const [strongestWindow, setStrongestWindow] = useState(null); // Best correlation time window
  const [corrWindow, setCorrWindow] = useState(3); // Correlation window size in 5-min intervals (default 15min = 3)
  const [lastLiveUpdate, setLastLiveUpdate] = useState(null);
  const [candleDirection, setCandleDirection] = useState(null); // 'up' or 'down'
  const [dotInterval, setDotInterval] = useState('1m'); // '1m' or '5m'
  const [backtestData, setBacktestData] = useState(null);
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [backtestPeriod, setBacktestPeriod] = useState('6m');
  const [showTradePanel, setShowTradePanel] = useState(false);
  const [predictorAccuracy, setPredictorAccuracy] = useState(null);
  const [predictorLoading, setPredictorLoading] = useState(false);
  const [rsiMfiAnalysis, setRsiMfiAnalysis] = useState(null);
  const [rsiMfiLoading, setRsiMfiLoading] = useState(false);
  const [priceRange, setPriceRange] = useState(null);
  const [alertsEnabled, setAlertsEnabled] = useState(() => {
    try { return localStorage.getItem('stockpimp-rsi-mfi-alerts') === '1'; } catch { return false; }
  });
  const lastStrengthRef = useRef(null);
  
  const BACKTEST_OPTIONS = [
    { label: '5Y', value: '5y', months: 60 },
    { label: '3Y', value: '3y', months: 36 },
    { label: '2Y', value: '2y', months: 24 },
    { label: '1Y', value: '1y', months: 12 },
    { label: '6M', value: '6m', months: 6 },
    { label: '3M', value: '3m', months: 3 },
    { label: '60D', value: '60d', months: 2 },
    { label: '30D', value: '30d', months: 1 },
    { label: '20D', value: '20d', months: 0, days: 20 },
    { label: '10D', value: '10d', months: 0, days: 10 },
    { label: '5D', value: '5d', months: 0, days: 5 },
    { label: '2D', value: '2d', months: 0, days: 2 },
  ];

  // Lightweight live refresh for the white line only (60s for 5m, 60s for 1m)
  // When viewing a historical `date`, we still hit the same endpoint but with
  // ?date=YYYY-MM-DD so the chart shows that day's price action — no live polling.
  const refreshWhiteLine = useCallback(async () => {
    if (!ticker) return;
    if (date) return;  // historical mode: don't poll for live updates
    try {
      const res = await axios.get(`${API_URL}/api/stock/intraday-live/${ticker}?dot_interval=${dotInterval}`, { timeout: 10000 });
      if (res.data?.chart_data?.length > 0) {
        setTodayData(res.data);
        setCandleDirection(res.data.candle_direction || null);
        setLastLiveUpdate(new Date());
      }
    } catch (err) {
      // Silent fail — don't disrupt the chart
    }
  }, [ticker, dotInterval, date]);

  const fetchData = useCallback(async () => {
    if (!ticker) return;
    
    setLoading(true);
    setError(null);
    
    // Fire patterns + live (or fallback) IN PARALLEL — they're independent.
    // Previously these were sequential awaits, adding ~2-3s to first paint.
    const patternsUrl = date
      ? `${API_URL}/api/stock/intraday-patterns/${ticker}?days=${selectedDays}&as_of_date=${date}`
      : `${API_URL}/api/stock/intraday-patterns/${ticker}?days=${selectedDays}`;
    const liveUrl = date
      ? `${API_URL}/api/stock/intraday-live/${ticker}?dot_interval=${dotInterval}&date=${date}`
      : `${API_URL}/api/stock/intraday-live/${ticker}?dot_interval=${dotInterval}`;

    const [patternsRes, liveRes] = await Promise.allSettled([
      axios.get(patternsUrl, { timeout: 30000 }),
      axios.get(liveUrl, { timeout: 15000 }),
    ]);

    if (patternsRes.status === 'fulfilled') {
      setData(patternsRes.value.data);
    } else {
      console.error('Error fetching intraday patterns:', patternsRes.reason);
      setError('Failed to load intraday patterns');
      setLoading(false);
      return;
    }

    if (liveRes.status === 'fulfilled') {
      setTodayData(liveRes.value.data);
      setCandleDirection(liveRes.value.data?.candle_direction || null);
      setLastLiveUpdate(new Date());
    } else {
      // Fallback to original endpoint on live failure
      try {
        const todayRes2 = await axios.get(`${API_URL}/api/stock/intraday-price-action/${ticker}`, { timeout: 20000 });
        setTodayData(todayRes2.data);
      } catch (fallbackErr) {
        console.warn('Today intraday data unavailable:', fallbackErr.message);
        setTodayData(null);
      }
    }

    setLoading(false);
  }, [ticker, selectedDays, date, dotInterval]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch backtest data when ticker, selectedDays, or backtestPeriod changes
  useEffect(() => {
    if (!ticker) return;
    const opt = BACKTEST_OPTIONS.find(o => o.value === backtestPeriod);
    if (!opt) return;
    const fetchBacktest = async () => {
      setBacktestLoading(true);
      try {
        const months = opt.months || Math.max(1, Math.ceil((opt.days || 30) / 30));
        const res = await axios.get(`${API_URL}/api/stock/backtest-pattern/${ticker}?days=${selectedDays}&months=${months}`, { timeout: 120000 });
        if (res.data && !res.data.error) {
          setBacktestData(res.data);
        }
      } catch (err) {
        // Silent fail
      } finally {
        setBacktestLoading(false);
      }
    };
    fetchBacktest();
  }, [ticker, selectedDays, backtestPeriod]);

  // Fetch 5-year predictor accuracy (one-shot per ticker)
  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    const fetchPredictorAcc = async () => {
      setPredictorLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/stock/predictor-accuracy/${ticker}?months=60`, { timeout: 90000 });
        if (!cancelled && res.data && !res.data.error) {
          setPredictorAccuracy(res.data);
        }
      } catch (err) {
        // Silent fail
      } finally {
        if (!cancelled) setPredictorLoading(false);
      }
    };
    fetchPredictorAcc();
    return () => { cancelled = true; };
  }, [ticker]);

  // Fetch realtime quote for today's high/low + 52W range bars
  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    const fetchRange = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/stock/realtime-quote/${ticker}`, { timeout: 20000 });
        if (!cancelled && res.data) {
          setPriceRange({
            current: res.data.currentPrice ?? res.data.price ?? null,
            dayLow: res.data.low ?? res.data.dayLow ?? null,
            dayHigh: res.data.high ?? res.data.dayHigh ?? null,
            wkLow: res.data.fiftyTwoWeekLow ?? null,
            wkHigh: res.data.fiftyTwoWeekHigh ?? null,
          });
        }
      } catch (_) { /* silent */ }
    };
    fetchRange();
    const id = setInterval(fetchRange, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, [ticker]);

  // Extract the most-recent live RSI / MFI / candle direction for the analysis panel.
  // Re-fetches when any of them change (debounced via cache_key bucketing on the backend).
  // NOTE: Moved below lastTodayIndex declaration to avoid TDZ errors.

  // Auto-refresh white line every 60 seconds during market hours
  // Disabled when viewing a historical date (data is static)
  useEffect(() => {
    if (!ticker || !data) return;
    if (date) return;  // historical mode: no live polling
    
    const interval = setInterval(() => {
      refreshWhiteLine();
    }, 30000); // 30 seconds — white line advances every minute
    
    return () => clearInterval(interval);
  }, [ticker, data, refreshWhiteLine, date]);

  // Re-fetch dot direction when interval toggle changes
  useEffect(() => {
    if (ticker && data) {
      refreshWhiteLine();
    }
  }, [dotInterval]); // eslint-disable-line

  // Calculate Pearson correlation coefficient for a given set of values
  const calculatePearsonCorrelation = useCallback((xValues, yValues) => {
    if (xValues.length < 3 || yValues.length < 3) return 0;
    
    const n = xValues.length;
    const sumX = xValues.reduce((a, b) => a + b, 0);
    const sumY = yValues.reduce((a, b) => a + b, 0);
    const sumXY = xValues.reduce((total, x, i) => total + x * yValues[i], 0);
    const sumX2 = xValues.reduce((total, x) => total + x * x, 0);
    const sumY2 = yValues.reduce((total, y) => total + y * y, 0);
    
    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
    
    if (denominator === 0) return 0;
    return numerator / denominator;
  }, []);

  // Calculate correlation between today's movement and historical pattern
  const calculateCorrelation = useCallback((chartData, todayChanges) => {
    if (!chartData || chartData.length === 0 || Object.keys(todayChanges).length === 0) {
      return null;
    }
    
    // Get paired data points
    const historicalValues = [];
    const todayValues = [];
    
    chartData.forEach(point => {
      if (todayChanges[point.time] !== undefined) {
        historicalValues.push(point.avgChange);
        todayValues.push(todayChanges[point.time]);
      }
    });
    
    if (historicalValues.length < 5) return null;
    
    const r = calculatePearsonCorrelation(historicalValues, todayValues);
    
    return {
      value: r,
      percentage: Math.abs(r * 100).toFixed(1),
      strength: Math.abs(r) >= 0.7 ? 'strong' : Math.abs(r) >= 0.4 ? 'moderate' : 'weak',
      direction: r >= 0 ? 'positive' : 'negative'
    };
  }, [calculatePearsonCorrelation]);

  // Calculate correlation for every N-interval window
  const calculateWindowCorrelations = useCallback((chartData, todayChanges) => {
    if (!chartData || chartData.length < corrWindow || Object.keys(todayChanges).length < corrWindow) {
      return [];
    }
    
    // Filter to only points where we have today's data
    const pairedData = chartData.filter(point => todayChanges[point.time] !== undefined);
    if (pairedData.length < corrWindow) return [];
    
    const correlations = [];
    const windowSize = corrWindow;
    
    // Calculate correlation for each window
    for (let i = 0; i <= pairedData.length - windowSize; i += windowSize) {
      const windowData = pairedData.slice(i, i + windowSize);
      
      if (windowData.length < windowSize) continue;
      
      const historicalValues = windowData.map(p => p.avgChange);
      const todayValues = windowData.map(p => todayChanges[p.time]);
      
      const correlation = calculatePearsonCorrelation(historicalValues, todayValues);
      const absCorrelation = Math.abs(correlation);
      
      correlations.push({
        startTime: windowData[0].time,
        endTime: windowData[windowData.length - 1].time,
        startIndex: i,
        endIndex: i + windowSize - 1,
        correlation: correlation,
        absCorrelation: absCorrelation,
        percentage: (absCorrelation * 100).toFixed(1),
        strength: absCorrelation >= 0.7 ? 'strong' : absCorrelation >= 0.4 ? 'moderate' : 'weak',
        direction: correlation >= 0 ? 'positive' : 'negative',
        label: `${windowData[0].time}-${windowData[windowData.length - 1].time}`
      });
    }
    
    return correlations;
  }, [calculatePearsonCorrelation, corrWindow]);

  // Find the best window from calculated correlations
  const findBestWindow = useCallback((correlations) => {
    if (!correlations || correlations.length === 0) return null;
    
    return correlations.reduce((best, current) => 
      current.absCorrelation > (best?.absCorrelation || 0) ? current : best
    , null);
  }, []);

  // Prepare chart data from the existing API format - use useMemo to prevent infinite re-renders
  const todayChanges = React.useMemo(() => {
    if (!todayData?.chart_data || todayData.chart_data.length === 0) return {};
    
    const chartDataRaw = todayData.chart_data;
    const openPrice = chartDataRaw[0]?.open;
    if (!openPrice) return {};
    
    const changes = {};
    
    // Group by 5-minute intervals
    const fiveMinData = {};
    
    chartDataRaw.forEach((candle) => {
      if (candle.time) {
        const timeMatch = candle.time.match(/(\d{1,2}):(\d{2})/);
        if (timeMatch) {
          const hour = parseInt(timeMatch[1]);
          const minute = parseInt(timeMatch[2]);
          
          // Use exact minute — 1-minute data, no rounding
          const timeKey = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          
          // Keep the last price for this 5-min interval
          if (!fiveMinData[timeKey]) {
            fiveMinData[timeKey] = [];
          }
          fiveMinData[timeKey].push(candle.price);
        }
      }
    });
    
    // Calculate percentage change from market open for each 5-min interval
    Object.keys(fiveMinData).forEach(timeKey => {
      const prices = fiveMinData[timeKey];
      if (prices.length > 0) {
        // Use the last price in the interval
        const lastPrice = prices[prices.length - 1];
        const pctChange = ((lastPrice - openPrice) / openPrice) * 100;
        changes[timeKey] = pctChange;
      }
    });
    
    return changes;
  }, [todayData]);

  // Today's running VWAP per 5-min bucket, expressed as % from today's open
  // (so it shares the same Y-axis as the historical % patterns and white line).
  const todayVwap = React.useMemo(() => {
    if (!todayData?.chart_data || todayData.chart_data.length === 0) return {};
    const chartDataRaw = todayData.chart_data;
    const openPrice = chartDataRaw[0]?.open || todayData.open_price;
    if (!openPrice) return {};

    const byBucket = {};
    let cumPV = 0;
    let cumV = 0;
    chartDataRaw.forEach((candle) => {
      const high = candle.high ?? candle.price ?? 0;
      const low = candle.low ?? candle.price ?? 0;
      const close = candle.close ?? candle.price ?? 0;
      const vol = candle.volume ?? 0;
      const tp = (high + low + close) / 3;
      if (vol > 0 && tp > 0) {
        cumPV += tp * vol;
        cumV += vol;
      }
      const vwap = cumV > 0 ? cumPV / cumV : tp;
      const t = (candle.time || "").trim();
      const m = t.match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return;
      const hh = String(parseInt(m[1], 10)).padStart(2, "0");
      const mm = parseInt(m[2], 10);
      const bucket = `${hh}:${String(Math.floor(mm / 5) * 5).padStart(2, "0")}`;
      // Keep the latest VWAP per bucket
      byBucket[bucket] = ((vwap / openPrice) - 1) * 100;
    });
    return byBucket;
  }, [todayData]);
  
  const chartData = React.useMemo(() => {
    if (!data?.patterns) return [];
    
    // 78 pattern slots ONLY — for bars, yellow line, volume display
    // todayChange is NOT included here (white line uses separate 1-minute data)
    const mapped = data.patterns.map(pattern => ({
      time: pattern.time_pst,
      timeEst: pattern.time_est,
      avgChange: pattern.avg_change,
      avgVolume: pattern.avg_volume,
      upPct: pattern.probability_up,
      downPct: pattern.probability_down,
      periods: pattern.total_periods,
      signal: pattern.recommendation,
      avgGain: pattern.avg_gain,
      avgLoss: pattern.avg_loss,
      fill: pattern.avg_change >= 0 ? '#22c55e' : '#ef4444'
    }));
    
    return mapped;
  }, [data]);

  // (Regression + S/R computed inside enrichedChartData/finalChartData memo above)

  // Enrich the 78-point chart with white line data (from 1-min source)
  const enrichedChartData = React.useMemo(() => {
    if (!chartData) return chartData;
    const sortedTodayKeys = Object.keys(todayChanges).sort();
    const lastActualMinute = sortedTodayKeys.length > 0 ? sortedTodayKeys[sortedTodayKeys.length - 1] : null;
    const sortedVwapKeys = Object.keys(todayVwap).sort();
    
    return chartData.map(d => {
      if (lastActualMinute && d.time > lastActualMinute) {
        return { ...d, todayChange: null, todayVwap: null };
      }
      
      let todayChange = todayChanges[d.time];
      if (todayChange === undefined && sortedTodayKeys.length > 0) {
        for (let i = sortedTodayKeys.length - 1; i >= 0; i--) {
          if (sortedTodayKeys[i] <= d.time) {
            todayChange = todayChanges[sortedTodayKeys[i]];
            break;
          }
        }
      }
      let vwapVal = todayVwap[d.time];
      if (vwapVal === undefined && sortedVwapKeys.length > 0) {
        for (let i = sortedVwapKeys.length - 1; i >= 0; i--) {
          if (sortedVwapKeys[i] <= d.time) {
            vwapVal = todayVwap[sortedVwapKeys[i]];
            break;
          }
        }
      }
      return {
        ...d,
        todayChange: todayChange !== undefined ? todayChange : null,
        todayVwap: vwapVal !== undefined ? vwapVal : null,
      };
    });
  }, [chartData, todayChanges, todayVwap]);

  // Compute REGRESSION + SUPPORT/RESISTANCE + RSI + MFI from the white line values
  const { finalChartData, supportLevel, resistanceLevel, regressionSlope, todayOpen, regressionEndPct } = React.useMemo(() => {
    if (!enrichedChartData) return { finalChartData: enrichedChartData, supportLevel: null, resistanceLevel: null, regressionSlope: 0, todayOpen: null, regressionEndPct: null };
    
    // Collect all points that have today's white line data
    const livePoints = [];
    enrichedChartData.forEach((d, i) => {
      if (d.todayChange !== null && d.todayChange !== undefined) {
        livePoints.push({ idx: i, value: d.todayChange });
      }
    });
    
    if (livePoints.length < 3) {
      return { finalChartData: enrichedChartData.map(d => ({ ...d, regressionValue: null, rsi: null, mfi: null })), supportLevel: null, resistanceLevel: null, regressionSlope: 0, todayOpen: null, regressionEndPct: null };
    }
    
    // Linear regression on the white line values
    const n = livePoints.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    livePoints.forEach((p, i) => {
      sumX += i; sumY += p.value; sumXY += i * p.value; sumX2 += i * i;
    });
    const denom = (n * sumX2 - sumX * sumX);
    const slope = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;
    const intercept = (sumY - slope * sumX) / n;
    
    // Support = true intraday LOW, Resistance = true intraday HIGH
    // Use raw 1-minute candle data for accurate min/max (not sampled 5-min values)
    const candles = todayData?.chart_data || [];
    let support, resistance;
    let openPriceOut = null;
    
    if (candles.length >= 3) {
      // Get opening price to compute % change for high/low
      const openPrice = candles[0].open || candles[0].price;
      if (openPrice > 0) {
        openPriceOut = openPrice;
        // Find the true intraday high and low from all 1-minute candles
        let maxPrice = -Infinity;
        let minPrice = Infinity;
        candles.forEach(c => {
          const high = c.high || c.price;
          const low = c.low || c.price;
          if (high > maxPrice) maxPrice = high;
          if (low < minPrice) minPrice = low;
        });
        resistance = ((maxPrice - openPrice) / openPrice) * 100;
        support = ((minPrice - openPrice) / openPrice) * 100;
      } else {
        // Fallback to sampled values
        const values = livePoints.map(p => p.value);
        support = Math.min(...values);
        resistance = Math.max(...values);
      }
    } else {
      // Fallback to sampled 5-min values
      const values = livePoints.map(p => p.value);
      support = Math.min(...values);
      resistance = Math.max(...values);
    }
    
    // ---- Compute RSI & MFI from todayData raw candles ----
    // (reuse candles already fetched above for S/R computation)
    // Build RSI (14-period) and MFI (14-period) per 5-min slot
    const rsiByTime = {};
    const mfiByTime = {};
    
    if (candles.length >= 14) {
      // Group candles into 5-minute buckets
      const fiveMinBuckets = {};
      candles.forEach(c => {
        if (!c.time) return;
        const match = c.time.match(/(\d{1,2}):(\d{2})/);
        if (!match) return;
        const h = parseInt(match[1]);
        const m = parseInt(match[2]);
        const roundedMin = Math.floor(m / 5) * 5;
        const key = `${h.toString().padStart(2, '0')}:${roundedMin.toString().padStart(2, '0')}`;
        if (!fiveMinBuckets[key]) fiveMinBuckets[key] = [];
        fiveMinBuckets[key].push(c);
      });
      
      // Build ordered 5-min OHLCV bars
      const sortedKeys = Object.keys(fiveMinBuckets).sort();
      const bars = sortedKeys.map(key => {
        const bucket = fiveMinBuckets[key];
        return {
          time: key,
          open: bucket[0].open || bucket[0].price,
          high: Math.max(...bucket.map(c => c.high || c.price)),
          low: Math.min(...bucket.map(c => c.low || c.price)),
          close: bucket[bucket.length - 1].price,
          volume: bucket.reduce((sum, c) => sum + (c.volume || 0), 0),
        };
      });
      
      // Compute RSI (14-period) for each bar
      const period = Math.min(14, Math.max(5, Math.floor(bars.length / 3)));
      for (let i = period; i < bars.length; i++) {
        let gains = 0, losses = 0;
        for (let j = i - period + 1; j <= i; j++) {
          const diff = bars[j].close - bars[j - 1].close;
          if (diff > 0) gains += diff;
          else losses += Math.abs(diff);
        }
        const avgGain = gains / period;
        const avgLoss = losses / period;
        const rs = avgLoss > 0 ? avgGain / avgLoss : 100;
        const rsi = 100 - (100 / (1 + rs));
        rsiByTime[bars[i].time] = Math.round(rsi * 100) / 100;
      }
      
      // Compute MFI (14-period) for each bar
      for (let i = period; i < bars.length; i++) {
        let posFlow = 0, negFlow = 0;
        for (let j = i - period + 1; j <= i; j++) {
          const tp = (bars[j].high + bars[j].low + bars[j].close) / 3;
          const prevTp = (bars[j - 1].high + bars[j - 1].low + bars[j - 1].close) / 3;
          const rawMF = tp * bars[j].volume;
          if (tp > prevTp) posFlow += rawMF;
          else if (tp < prevTp) negFlow += rawMF;
        }
        const mfRatio = negFlow > 0 ? posFlow / negFlow : 100;
        const mfi = 100 - (100 / (1 + mfRatio));
        mfiByTime[bars[i].time] = Math.round(mfi * 100) / 100;
      }
    }
    
    // Map regression + RSI + MFI back onto enriched data
    // Also PROJECT the regression line forward past the last live slot to EOD
    // so users can see where the current trend extrapolates.
    let liveIdx = 0;
    let lastLiveArrayIdx = -1;
    const final = enrichedChartData.map((d, arrIdx) => {
      const rsi = rsiByTime[d.time] !== undefined ? rsiByTime[d.time] : null;
      const mfi = mfiByTime[d.time] !== undefined ? mfiByTime[d.time] : null;
      
      if (d.todayChange !== null && d.todayChange !== undefined) {
        const regVal = intercept + slope * liveIdx;
        liveIdx++;
        lastLiveArrayIdx = arrIdx;
        return { ...d, regressionValue: regVal, rsi, mfi };
      }
      return { ...d, regressionValue: null, rsi, mfi };
    });

    // Extend regression forward: for every slot AFTER the last live slot,
    // continue the regression line using the same slope/intercept.
    if (lastLiveArrayIdx >= 0 && n >= 3) {
      for (let i = lastLiveArrayIdx + 1; i < final.length; i++) {
        const stepsBeyond = i - lastLiveArrayIdx;
        const extrapolated = intercept + slope * (n - 1 + stepsBeyond);
        final[i] = { ...final[i], regressionValue: extrapolated };
      }
    }

    // EOD regression % value (at the last slot of the session)
    const regEndPct = final.length > 0 ? final[final.length - 1].regressionValue : null;
    
    return { finalChartData: final, supportLevel: support, resistanceLevel: resistance, regressionSlope: slope, todayOpen: openPriceOut, regressionEndPct: regEndPct };
  }, [enrichedChartData, todayData]);

  // Find the index of the last data point with today's data (for the pulsing dot)
  const lastTodayIndex = React.useMemo(() => {
    if (!finalChartData || finalChartData.length === 0) return -1;
    for (let i = finalChartData.length - 1; i >= 0; i--) {
      if (finalChartData[i].todayChange !== null && finalChartData[i].todayChange !== undefined) {
        return i;
      }
    }
    return -1;
  }, [finalChartData]);

  const hasTodayData = Object.keys(todayChanges).length > 0;

  // Extract the most-recent live RSI / MFI / candle direction for the analysis panel.
  const { currentRsi, currentMfi, currentCandleDirection } = React.useMemo(() => {
    if (!finalChartData || lastTodayIndex < 0) {
      return { currentRsi: null, currentMfi: null, currentCandleDirection: 'neutral' };
    }
    let rsiVal = null, mfiVal = null;
    for (let i = lastTodayIndex; i >= 0; i--) {
      if (rsiVal === null && finalChartData[i].rsi != null) rsiVal = finalChartData[i].rsi;
      if (mfiVal === null && finalChartData[i].mfi != null) mfiVal = finalChartData[i].mfi;
      if (rsiVal !== null && mfiVal !== null) break;
    }
    let candleDir = 'neutral';
    const livePts = [];
    for (let i = lastTodayIndex; i >= 0 && livePts.length < 2; i--) {
      if (finalChartData[i].todayChange != null) livePts.unshift(finalChartData[i].todayChange);
    }
    if (livePts.length >= 2) {
      const d = livePts[1] - livePts[0];
      if (d > 0.02) candleDir = 'bullish';
      else if (d < -0.02) candleDir = 'bearish';
    }
    return { currentRsi: rsiVal, currentMfi: mfiVal, currentCandleDirection: candleDir };
  }, [finalChartData, lastTodayIndex]);

  useEffect(() => {
    if (!ticker || currentRsi == null || currentMfi == null) return;
    let cancelled = false;
    const fetchAnalysis = async () => {
      setRsiMfiLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/stock/rsi-mfi-analysis/${ticker}`,
          { params: { rsi: currentRsi, mfi: currentMfi, candle_direction: currentCandleDirection, horizon_bars: 5 }, timeout: 45000 }
        );
        if (!cancelled && res.data && !res.data.error) {
          setRsiMfiAnalysis(res.data);
        }
      } catch (err) { /* silent */ }
      finally { if (!cancelled) setRsiMfiLoading(false); }
    };
    fetchAnalysis();
    return () => { cancelled = true; };
  }, [ticker, currentRsi && Math.round(currentRsi / 5) * 5, currentMfi && Math.round(currentMfi / 5) * 5, currentCandleDirection]);

  // Divergence detection — compare price vs RSI/MFI slopes over the live segment
  const divergenceInfo = React.useMemo(() => {
    if (!finalChartData || lastTodayIndex < 5) return null;
    const prices = [], rsis = [], mfis = [];
    for (let i = 0; i <= lastTodayIndex; i++) {
      if (finalChartData[i].todayChange != null) prices.push(finalChartData[i].todayChange);
      if (finalChartData[i].rsi != null) rsis.push(finalChartData[i].rsi);
      if (finalChartData[i].mfi != null) mfis.push(finalChartData[i].mfi);
    }
    if (prices.length < 6 || rsis.length < 6 || mfis.length < 6) return null;
    const slope = (arr) => {
      const n = arr.length;
      let sx = 0, sy = 0, sxy = 0, sxx = 0;
      arr.forEach((y, x) => { sx += x; sy += y; sxy += x * y; sxx += x * x; });
      const d = n * sxx - sx * sx;
      return d === 0 ? 0 : (n * sxy - sx * sy) / d;
    };
    const priceSlope = slope(prices);
    const rsiSlope = slope(rsis);
    const mfiSlope = slope(mfis);
    const results = [];
    if (priceSlope > 0.01 && rsiSlope < -0.05) results.push({ type: 'BEARISH_DIVERGENCE', indicator: 'RSI', desc: 'Price rising but RSI falling — buying momentum weakening, reversal risk.' });
    if (priceSlope > 0.01 && mfiSlope < -0.05) results.push({ type: 'BEARISH_DIVERGENCE', indicator: 'MFI', desc: 'Price rising but MFI falling — money flow weakening, rally losing fuel.' });
    if (priceSlope < -0.01 && rsiSlope > 0.05) results.push({ type: 'BULLISH_DIVERGENCE', indicator: 'RSI', desc: 'Price falling but RSI rising — selling exhausting, bounce setup.' });
    if (priceSlope < -0.01 && mfiSlope > 0.05) results.push({ type: 'BULLISH_DIVERGENCE', indicator: 'MFI', desc: 'Price falling but MFI rising — money flowing IN despite drop, bounce setup.' });
    return { priceSlope, rsiSlope, mfiSlope, results };
  }, [finalChartData, lastTodayIndex]);

  // Play a short WebAudio beep (no external asset needed)
  const playAlertBeep = useCallback(() => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1320, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.5);
    } catch (_) { /* silent */ }
  }, []);

  // Fire alert when strength flips to an extreme and alerts are enabled
  useEffect(() => {
    if (!rsiMfiAnalysis?.strength) return;
    const s = rsiMfiAnalysis.strength;
    const prev = lastStrengthRef.current;
    lastStrengthRef.current = s;
    if (!alertsEnabled) return;
    if (prev === null) return; // Don't fire on first load
    if (s === prev) return;
    const isExtreme = s.startsWith('EXTREMELY');
    if (!isExtreme) return;
    const isWeak = s.includes('WEAK');
    const body = isWeak
      ? 'RSI + MFI collapsed — possible bullish reversal / bounce zone.'
      : 'RSI + MFI running hot — exhaustion / pullback risk.';
    playAlertBeep();
    toast[isWeak ? 'error' : 'success'](
      `${ticker}: ${s}`,
      { description: body, duration: 8000 }
    );
    // Fire browser notification (works on desktop + installed iOS PWA + Android Chrome)
    sendNotification({
      title: `${ticker} — ${s}`,
      body,
      tag: `strength-${ticker}`,
      onClick: () => { /* tab focus only */ },
    });
  }, [rsiMfiAnalysis?.strength, alertsEnabled, ticker, playAlertBeep]);

  // Fire alert on NEW divergence detected
  const lastDivergenceRef = useRef('');
  useEffect(() => {
    if (!alertsEnabled || !divergenceInfo?.results?.length) return;
    const signature = divergenceInfo.results.map(r => `${r.type}:${r.indicator}`).sort().join('|');
    if (signature === lastDivergenceRef.current) return;
    lastDivergenceRef.current = signature;
    const first = divergenceInfo.results[0];
    const isBullish = first.type === 'BULLISH_DIVERGENCE';
    playAlertBeep();
    toast[isBullish ? 'success' : 'error'](
      `${ticker}: ${first.indicator} ${isBullish ? 'BULLISH' : 'BEARISH'} DIVERGENCE`,
      { description: first.desc, duration: 8000 }
    );
    sendNotification({
      title: `${ticker} — ${first.indicator} ${isBullish ? 'BULLISH' : 'BEARISH'} DIVERGENCE`,
      body: first.desc,
      tag: `divergence-${ticker}`,
    });
  }, [divergenceInfo?.results, alertsEnabled, ticker, playAlertBeep]);

  const toggleAlerts = useCallback(async () => {
    const next = !alertsEnabled;
    if (next) {
      // User is turning ON → request browser-notification permission
      const supported = isNotificationSupported();
      const status = getNotificationStatus();
      const iosHint = getIOSInstallHint();

      if (iosHint) {
        toast.warning('iPhone detected', { description: iosHint, duration: 15000 });
      }

      if (supported && status !== 'granted') {
        const result = await requestNotificationPermission();
        if (result === 'granted') {
          toast.success('Browser notifications granted', {
            description: 'You will get native notifications even when this tab is in the background.',
          });
          sendNotification({
            title: 'StockPimp alerts active',
            body: `Strength & divergence alerts enabled for ${ticker}.`,
            tag: 'setup-confirm',
          });

          // Register service worker + subscribe to Web Push (iOS PWA + Android + desktop)
          try {
            await registerServiceWorker();
            const sub = await subscribeToPush({ apiBase: API_URL, tickers: [ticker] });
            if (sub) {
              toast.success('Push notifications enabled', {
                description: `Server-side push active for ${ticker}. You'll receive alerts even when the app is closed.`,
                duration: 8000,
              });
            }
          } catch (pushErr) {
            toast.warning('Push unavailable', {
              description: 'In-app alerts still work, but background push failed to register.',
            });
          }
        } else if (result === 'denied') {
          toast.error('Browser notifications blocked', {
            description: 'Enable notifications in your browser settings to get background alerts. In-app toasts still work.',
            duration: 10000,
          });
        }
      } else if (supported && status === 'granted') {
        // Already granted — still make sure we're subscribed to push for THIS ticker
        try {
          await registerServiceWorker();
          await subscribeToPush({ apiBase: API_URL, tickers: [ticker] });
        } catch { /* silent */ }
      }
    } else {
      // Turning OFF — unsubscribe from server-side push
      try {
        await unsubscribeFromPush({ apiBase: API_URL });
      } catch { /* silent */ }
    }

    setAlertsEnabled(next);
    try { localStorage.setItem('stockpimp-rsi-mfi-alerts', next ? '1' : '0'); } catch (_) { /* silent */ }
    if (next) {
      playAlertBeep();
      toast.success('Strength alerts ON', { description: 'You will be notified on extreme strength flips and divergences.' });
    } else {
      toast('Strength alerts OFF');
    }
  }, [alertsEnabled, ticker, playAlertBeep]);

  // 15-MINUTE REGRESSION PREDICTOR — fit a slope on the LAST 3 white-line slot
  // values (≈15 minutes of today's price action) and project forward to EOD.
  // Produces `predictor15Value` on every slot from lastTodayIndex → end-of-session.
  const { chartDataWithPredictor, predictor15Slope, predictor15LastValue, predictor15EndValue } = React.useMemo(() => {
    if (!finalChartData || finalChartData.length === 0 || lastTodayIndex < 0) {
      return {
        chartDataWithPredictor: finalChartData,
        predictor15Slope: null,
        predictor15LastValue: null,
        predictor15EndValue: null,
      };
    }

    // Collect the last 3 live values (15 minutes @ 5-min slots)
    const livePoints = [];
    for (let i = lastTodayIndex; i >= 0 && livePoints.length < 3; i--) {
      const v = finalChartData[i].todayChange;
      if (v !== null && v !== undefined) livePoints.unshift(v);
    }

    if (livePoints.length < 2) {
      return {
        chartDataWithPredictor: finalChartData.map(d => ({ ...d, predictor15Value: null })),
        predictor15Slope: null,
        predictor15LastValue: null,
        predictor15EndValue: null,
      };
    }

    // Least-squares slope on x=[0..n-1], y=livePoints
    const n = livePoints.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    livePoints.forEach((y, i) => {
      sumX += i; sumY += y; sumXY += i * y; sumX2 += i * i;
    });
    const denom = n * sumX2 - sumX * sumX;
    const slope = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;
    const intercept = (sumY - slope * sumX) / n;

    // Anchor: the predictor line at the last live slot MUST equal the last
    // white-line value exactly (so the user sees the projection extend from
    // where price actually is right now).
    const anchorX = n - 1;
    const anchorVal = livePoints[livePoints.length - 1];

    const withPredictor = finalChartData.map((d, idx) => {
      if (idx < lastTodayIndex) {
        return { ...d, predictor15Value: null };
      }
      // Steps beyond the anchor slot
      const stepsBeyond = idx - lastTodayIndex;
      const predVal = anchorVal + slope * stepsBeyond;
      return { ...d, predictor15Value: predVal };
    });

    const endValue = withPredictor[withPredictor.length - 1]?.predictor15Value ?? null;

    return {
      chartDataWithPredictor: withPredictor,
      predictor15Slope: slope,
      predictor15LastValue: anchorVal,
      predictor15EndValue: endValue,
    };
  }, [finalChartData, lastTodayIndex]);

  // Determine options signal: CALL (green), PUT (red), HOLD (white)
  // Uses ALL chart data: RSI, MFI, regression slope, S/R position, 
  // historical pattern (avgChange/upPct), correlation match, candle direction
  const optionsSignal = React.useMemo(() => {
    if (!finalChartData || !hasTodayData) return null;
    
    // Find the current slot (last slot with today's data)
    let currentSlot = null;
    let latestRsi = null;
    let latestMfi = null;
    let latestChange = null;
    
    for (let i = finalChartData.length - 1; i >= 0; i--) {
      const d = finalChartData[i];
      if (d.todayChange !== null && d.todayChange !== undefined) {
        if (!currentSlot) currentSlot = d;
        if (latestChange === null) latestChange = d.todayChange;
      }
      if (latestRsi === null && d.rsi !== null && d.rsi !== undefined) latestRsi = d.rsi;
      if (latestMfi === null && d.mfi !== null && d.mfi !== undefined) latestMfi = d.mfi;
      if (currentSlot && latestRsi !== null && latestMfi !== null) break;
    }
    
    if (!currentSlot) return null;
    
    let bullScore = 0;
    let bearScore = 0;
    
    // === 1. RSI Signal (weight: 3) ===
    if (latestRsi !== null) {
      if (latestRsi < 25) bullScore += 4;          // Deep oversold — strong CALL
      else if (latestRsi < 30) bullScore += 3;     // Oversold
      else if (latestRsi < 40) bullScore += 2;     // Approaching oversold
      else if (latestRsi < 50) bullScore += 1;     // Below midline
      else if (latestRsi > 75) bearScore += 4;     // Deep overbought — strong PUT
      else if (latestRsi > 70) bearScore += 3;     // Overbought
      else if (latestRsi > 60) bearScore += 2;     // Approaching overbought
      else if (latestRsi > 50) bearScore += 1;     // Above midline
    }
    
    // === 2. MFI Signal (weight: 3) ===
    if (latestMfi !== null) {
      if (latestMfi < 15) bullScore += 4;          // Extreme money flow oversold
      else if (latestMfi < 20) bullScore += 3;
      else if (latestMfi < 35) bullScore += 1;
      else if (latestMfi > 85) bearScore += 4;     // Extreme money flow overbought
      else if (latestMfi > 80) bearScore += 3;
      else if (latestMfi > 65) bearScore += 1;
    }
    
    // === 3. Regression Slope — trend direction (weight: 3) ===
    if (regressionSlope > 0.01) bullScore += 3;       // Strong uptrend
    else if (regressionSlope > 0.003) bullScore += 2;  // Moderate uptrend
    else if (regressionSlope > 0) bullScore += 1;      // Slight uptrend
    else if (regressionSlope < -0.01) bearScore += 3;  // Strong downtrend
    else if (regressionSlope < -0.003) bearScore += 2; // Moderate downtrend
    else if (regressionSlope < 0) bearScore += 1;      // Slight downtrend
    
    // === 4. Price Position vs Support/Resistance (weight: 2) ===
    if (supportLevel !== null && resistanceLevel !== null && latestChange !== null) {
      const range = resistanceLevel - supportLevel;
      if (range > 0) {
        const position = (latestChange - supportLevel) / range; // 0 = at support, 1 = at resistance
        if (position < 0.15) bullScore += 3;       // At support = reversal up likely (CALL)
        else if (position < 0.30) bullScore += 2;
        else if (position > 0.85) bearScore += 3;  // At resistance = reversal down likely (PUT)
        else if (position > 0.70) bearScore += 2;
      }
    }
    
    // === 5. Historical Pattern — avgChange and upPct at current time slot (weight: 2) ===
    if (currentSlot.avgChange !== undefined) {
      if (currentSlot.avgChange > 0.1) bullScore += 2;      // Historically bullish slot
      else if (currentSlot.avgChange > 0.02) bullScore += 1;
      else if (currentSlot.avgChange < -0.1) bearScore += 2; // Historically bearish slot
      else if (currentSlot.avgChange < -0.02) bearScore += 1;
    }
    if (currentSlot.upPct !== undefined) {
      if (currentSlot.upPct >= 70) bullScore += 2;           // 70%+ of days were up at this time
      else if (currentSlot.upPct >= 60) bullScore += 1;
      else if (currentSlot.upPct <= 30) bearScore += 2;      // 70%+ of days were down at this time
      else if (currentSlot.upPct <= 40) bearScore += 1;
    }
    
    // === 6. Correlation Match — how closely today follows historical pattern (weight: 2) ===
    if (correlation !== null) {
      // If correlation is high positive, historical pattern is predictive
      if (correlation > 0.7) {
        // Today matches history — use the upcoming historical slots for direction
        const nextSlots = finalChartData.filter((d, i) => i > lastTodayIndex && i <= lastTodayIndex + 6);
        const avgUpcoming = nextSlots.length > 0 
          ? nextSlots.reduce((sum, d) => sum + (d.avgChange || 0), 0) / nextSlots.length 
          : 0;
        if (avgUpcoming > 0.02) bullScore += 2;   // History predicts upward continuation
        else if (avgUpcoming < -0.02) bearScore += 2;
      } else if (correlation < -0.5) {
        // Inverse correlation — today is doing the opposite of history
        const nextSlots = finalChartData.filter((d, i) => i > lastTodayIndex && i <= lastTodayIndex + 6);
        const avgUpcoming = nextSlots.length > 0 
          ? nextSlots.reduce((sum, d) => sum + (d.avgChange || 0), 0) / nextSlots.length 
          : 0;
        // Inverse: if history predicts up, today goes down
        if (avgUpcoming > 0.02) bearScore += 1;
        else if (avgUpcoming < -0.02) bullScore += 1;
      }
    }
    
    // === 7. Strongest Correlation Window (weight: 2) ===
    if (strongestWindow && strongestWindow.percentage >= 70) {
      // Strong pattern match in the best window — gives conviction
      if (strongestWindow.direction === 'positive') {
        // Pattern is reinforcing — look at the window's historical direction
        if (currentSlot.avgChange > 0) bullScore += 2;
        else bearScore += 2;
      }
    }
    
    // === 8. Real-time 1-min Candle Direction — immediate momentum (weight: 1) ===
    if (candleDirection === 'up') bullScore += 1;
    else if (candleDirection === 'down') bearScore += 1;
    
    // === 9. Price Momentum — compare current to regression (weight: 2) ===
    if (currentSlot.regressionValue !== null && currentSlot.regressionValue !== undefined && latestChange !== null) {
      const deviation = latestChange - currentSlot.regressionValue;
      if (deviation > 0.1) bearScore += 1;          // Price above regression = overextended (mean revert PUT)
      else if (deviation < -0.1) bullScore += 1;     // Price below regression = undervalued (mean revert CALL)
    }
    
    // Final decision
    const diff = bullScore - bearScore;
    if (diff >= 3) return 'CALL';
    if (diff <= -3) return 'PUT';
    return 'HOLD';
  }, [finalChartData, hasTodayData, regressionSlope, supportLevel, resistanceLevel, 
      correlation, strongestWindow, candleDirection, lastTodayIndex]);

  // Custom dot renderer: pulsing dot color based on options signal
  const renderLiveDot = useCallback((props) => {
    const { cx, cy, index, value } = props;
    if (index !== lastTodayIndex || value === null || value === undefined) return null;
    
    // Color based on options signal
    let dotColor, glowColor;
    if (optionsSignal === 'CALL') {
      dotColor = '#22c55e';
      glowColor = 'rgba(34,197,94,0.6)';
    } else if (optionsSignal === 'PUT') {
      dotColor = '#ef4444';
      glowColor = 'rgba(239,68,68,0.6)';
    } else {
      dotColor = '#ffffff';
      glowColor = 'rgba(255,255,255,0.6)';
    }
    
    return (
      <g key={`live-dot-${index}`}>
        <circle cx={cx} cy={cy} r={12} fill="none" stroke={dotColor} strokeWidth={2} opacity={0.4}>
          <animate attributeName="r" from="6" to="16" dur="1.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" from="0.6" to="0" dur="1.5s" repeatCount="indefinite" />
        </circle>
        <circle cx={cx} cy={cy} r={8} fill="none" stroke={dotColor} strokeWidth={1.5} opacity={0.3}>
          <animate attributeName="r" from="4" to="12" dur="1.5s" begin="0.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" from="0.5" to="0" dur="1.5s" begin="0.5s" repeatCount="indefinite" />
        </circle>
        <circle cx={cx} cy={cy} r={6} fill={glowColor} opacity={0.5}>
          <animate attributeName="opacity" values="0.3;0.7;0.3" dur="1s" repeatCount="indefinite" />
        </circle>
        <circle cx={cx} cy={cy} r={4} fill={dotColor} stroke="#000" strokeWidth={1} />
      </g>
    );
  }, [lastTodayIndex, optionsSignal]);

  // Calculate correlation when data or window size changes
  useEffect(() => {
    if (chartData.length > 0 && Object.keys(todayChanges).length > 0) {
      const corr = calculateCorrelation(chartData, todayChanges);
      setCorrelation(corr);
      
      // Calculate window correlations
      const windowCorrs = calculateWindowCorrelations(chartData, todayChanges);
      setCorrelations30Min(windowCorrs);
      
      // Find the best window
      const bestWindow = findBestWindow(windowCorrs);
      setStrongestWindow(bestWindow);
    } else {
      setCorrelation(null);
      setCorrelations30Min([]);
      setStrongestWindow(null);
    }
  }, [chartData, todayChanges, calculateCorrelation, calculateWindowCorrelations, findBestWindow]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4 shadow-lg min-w-[280px]">
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-700">
            <span className="text-lg font-semibold text-white">{data.time} PST</span>
            <span className="text-sm text-gray-400">({data.timeEst} EST)</span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Hist. Avg Change:</span>
              <span className={data.avgChange >= 0 ? 'text-green-400 font-medium' : 'text-red-400 font-medium'}>
                {data.avgChange >= 0 ? '+' : ''}{data.avgChange?.toFixed(4)}%
              </span>
            </div>
            {data.todayChange !== null && (
              <div className="flex justify-between">
                <span className="text-white font-medium">Today's Change:</span>
                <span className={`font-bold text-base ${data.todayChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {data.todayChange >= 0 ? '+' : ''}{data.todayChange?.toFixed(4)}%
                </span>
              </div>
            )}
            {data.regressionValue !== null && data.regressionValue !== undefined && (
              <div className="flex justify-between">
                <span className="text-orange-400">Regression:</span>
                <span className="text-orange-300">{data.regressionValue >= 0 ? '+' : ''}{data.regressionValue?.toFixed(4)}%</span>
              </div>
            )}
            {data.rsi !== null && data.rsi !== undefined && (
              <div className="flex justify-between">
                <span className="text-red-400">RSI (14):</span>
                <span className={`font-medium ${data.rsi > 70 ? 'text-red-400' : data.rsi < 30 ? 'text-green-400' : 'text-gray-300'}`}>
                  {data.rsi.toFixed(1)} {data.rsi > 70 ? '(OB)' : data.rsi < 30 ? '(OS)' : ''}
                </span>
              </div>
            )}
            {data.mfi !== null && data.mfi !== undefined && (
              <div className="flex justify-between">
                <span className="text-green-400">MFI (14):</span>
                <span className={`font-medium ${data.mfi > 80 ? 'text-red-400' : data.mfi < 20 ? 'text-green-400' : 'text-gray-300'}`}>
                  {data.mfi.toFixed(1)} {data.mfi > 80 ? '(OB)' : data.mfi < 20 ? '(OS)' : ''}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-400">Avg Volume:</span>
              <span className="text-blue-400">{data.avgVolume?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Up Days:</span>
              <span className="text-green-400">{data.upPct?.toFixed(2)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Down Days:</span>
              <span className="text-red-400">{data.downPct?.toFixed(2)}%</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-gray-700">
              <span className="text-gray-400">Signal:</span>
              <span className={`px-3 py-1 rounded text-sm font-medium ${
                data.signal === 'BUY' ? 'bg-green-900/50 text-green-400' :
                data.signal === 'SELL' ? 'bg-red-900/50 text-red-400' :
                'bg-gray-700 text-gray-300'
              }`}>
                {data.signal}
              </span>
            </div>
            {optionsSignal && (
              <div className="flex justify-between pt-1">
                <span className="text-gray-400">Options:</span>
                <span className={`px-3 py-1 rounded text-sm font-bold ${
                  optionsSignal === 'CALL' ? 'bg-green-900/60 text-green-400 border border-green-600/40' :
                  optionsSignal === 'PUT' ? 'bg-red-900/60 text-red-400 border border-red-600/40' :
                  'bg-gray-700 text-white border border-gray-500/40'
                }`}>
                  {optionsSignal}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  // Get signal icon
  const getSignalIcon = (signal) => {
    switch (signal) {
      case 'BUY':
        return <TrendingUp className="w-3 h-3 text-green-400" />;
      case 'SELL':
        return <TrendingDown className="w-3 h-3 text-red-400" />;
      default:
        return <Minus className="w-3 h-3 text-gray-400" />;
    }
  };

  // Loading skeleton
  if (loading && !data) {
    return (
      <div className="bg-[#0d0d1a] rounded-lg p-4 border border-gray-800">
        <div className="animate-pulse space-y-4">
          <div className="flex items-center justify-between">
            <div className="h-5 w-64 bg-gray-700 rounded"></div>
            <div className="h-6 w-20 bg-gray-700 rounded"></div>
          </div>
          <div className="h-[300px] bg-gray-800 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#0d0d1a] rounded-lg p-4 border border-slate-800" data-testid="intraday-patterns-skeleton">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-block w-2 h-2 rounded-full bg-amber-400/60 animate-pulse" />
          <span className="text-[11px] text-amber-300/80">Refreshing intraday patterns…</span>
        </div>
        <div className="h-[260px] bg-slate-800/40 rounded animate-pulse" />
      </div>
    );
  }

  const totalIntervals = data?.patterns?.length || 0;

  return (
    <div className="bg-[#0d0d1a] rounded-lg p-4 border border-gray-800 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Clock className="w-6 h-6 text-cyan-400" />
          <h3 className="text-xl font-semibold text-white">
            Intraday Time-of-Day Patterns
          </h3>
          {date ? (
            <span className="text-sm font-semibold text-fuchsia-300 bg-fuchsia-950/40 border border-fuchsia-700/50 px-3 py-1 rounded" data-testid="intraday-historical-badge">
              📅 Historical: {date}
            </span>
          ) : null}
          <span className="text-base text-gray-400 bg-gray-800 px-3 py-1 rounded">
            5-min intervals • {selectedDays} days{date ? ` ending before ${date}` : ""}
          </span>
          <FreshnessDot timestamp={lastLiveUpdate} size="sm" showLabel={true} />
          {totalIntervals > 0 && (
            <span className="text-base text-cyan-400">
              ({totalIntervals} intervals)
            </span>
          )}
        </div>
        
        <div className="space-y-2">
          {/* Row 1: Timeframe buttons + badges + controls */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Timeframe Selector Buttons */}
            <div className="flex items-center bg-gray-800 rounded border border-gray-700">
              {TIMEFRAME_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedDays(option.value)}
                  className={`px-4 py-2 text-base font-medium transition-colors ${
                    selectedDays === option.value
                      ? 'bg-cyan-600 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                  data-testid={`intraday-days-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          
            {/* Correlation Badge */}
            {correlation && viewMode === 'chart' && (
              <div className={`flex items-center gap-2 px-3 py-2 rounded text-base ${
                correlation.strength === 'strong' 
                  ? 'bg-green-900/40 border border-green-700/50 text-green-400'
                  : correlation.strength === 'moderate'
                  ? 'bg-yellow-900/40 border border-yellow-700/50 text-yellow-400'
                  : 'bg-gray-800 border border-gray-700 text-gray-400'
              }`}>
                <Activity className="w-5 h-5" />
                <span>
                  Overall: <strong>{correlation.percentage}%</strong>
                </span>
              </div>
            )}
          
            {/* Best Window Badge */}
            {strongestWindow && viewMode === 'chart' && (
              <div className={`flex items-center gap-2 px-3 py-2 rounded text-base ${
                strongestWindow.strength === 'strong' 
                  ? 'bg-green-900/40 border border-green-700/50 text-green-400'
                  : strongestWindow.strength === 'moderate'
                  ? 'bg-yellow-900/40 border border-yellow-700/50 text-yellow-400'
                  : 'bg-gray-800 border border-gray-700 text-gray-400'
              }`} title={`${corrWindow * 5}-min window from ${strongestWindow.startTime} to ${strongestWindow.endTime} PST`}>
                <TrendingUp className="w-5 h-5" />
                <span>
                  Best {corrWindow * 5}min: <strong>{strongestWindow.percentage}%</strong>
                  {' '}({strongestWindow.startTime}-{strongestWindow.endTime})
                </span>
              </div>
            )}

            {/* Options Signal Badge */}
            {optionsSignal && viewMode === 'chart' && (
              <div className={`flex items-center gap-2 px-3 py-2 rounded text-base font-bold ${
                optionsSignal === 'CALL' 
                  ? 'bg-green-900/50 border border-green-500/60 text-green-400'
                  : optionsSignal === 'PUT'
                  ? 'bg-red-900/50 border border-red-500/60 text-red-400'
                  : 'bg-gray-800 border border-gray-600 text-white'
              }`} data-testid="options-signal-badge">
                <span className={`w-3 h-3 rounded-full ${
                  optionsSignal === 'CALL' ? 'bg-green-400' : optionsSignal === 'PUT' ? 'bg-red-400' : 'bg-white'
                } animate-pulse`}></span>
                <span>{optionsSignal === 'CALL' ? 'CALL' : optionsSignal === 'PUT' ? 'PUT' : 'HOLD'}</span>
              </div>
            )}

            {/* Backtest Accuracy with Period Dropdown */}
            {viewMode === 'chart' && (
              <div className={`flex items-center gap-1 rounded text-sm ${
                backtestData 
                  ? backtestData.overall_accuracy >= 55 
                    ? 'bg-green-900/40 border border-green-700/50'
                    : backtestData.overall_accuracy >= 45
                    ? 'bg-yellow-900/40 border border-yellow-700/50'
                    : 'bg-red-900/40 border border-red-700/50'
                  : 'bg-gray-800 border border-gray-700'
              }`} data-testid="backtest-section">
                {backtestLoading ? (
                  <div className="flex items-center gap-1 px-3 py-2 text-gray-400">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    <span className="text-xs">Testing...</span>
                  </div>
                ) : backtestData ? (
                  <span className={`px-2 py-2 font-bold ${
                    backtestData.overall_accuracy >= 55 ? 'text-green-400' 
                    : backtestData.overall_accuracy >= 45 ? 'text-yellow-400' 
                    : 'text-red-400'
                  }`} title={`${backtestData.total_trading_days_tested} days, ${backtestData.intraday_total_slots_tested} slots tested`}>
                    {backtestData.overall_accuracy}%
                  </span>
                ) : (
                  <span className="px-2 py-2 text-gray-500">--%</span>
                )}
                <select
                  value={backtestPeriod}
                  onChange={(e) => setBacktestPeriod(e.target.value)}
                  className="bg-transparent text-xs py-2 pr-2 pl-0 border-none outline-none cursor-pointer text-gray-400 hover:text-white"
                  data-testid="backtest-period-select"
                >
                  {BACKTEST_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value} className="bg-gray-900 text-white">
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* View toggle */}
            <div className="flex items-center bg-gray-800 rounded border border-gray-700 ml-auto">
              <button
                onClick={() => setViewMode('chart')}
                className={`p-2.5 ${viewMode === 'chart' ? 'text-cyan-400 bg-gray-700' : 'text-gray-500 hover:text-gray-300'}`}
                title="Chart view"
                data-testid="intraday-chart-view"
              >
                <BarChart3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2.5 ${viewMode === 'table' ? 'text-cyan-400 bg-gray-700' : 'text-gray-500 hover:text-gray-300'}`}
                title="Table view"
                data-testid="intraday-table-view"
              >
                <Table className="w-5 h-5" />
              </button>
            </div>
          
            {/* Refresh button + LIVE indicator + Dot interval toggle */}
            {/* Only show LIVE controls when NOT in historical mode (no date prop) */}
            {hasTodayData && !date && (
              <div className="flex items-center gap-1.5">
                {/* Dot interval toggle */}
                <div className="flex items-center bg-gray-800 rounded border border-gray-700" data-testid="dot-interval-toggle">
                  <button
                    onClick={() => setDotInterval('5m')}
                    className={`px-2 py-1 text-xs font-medium transition-colors rounded-l ${
                      dotInterval === '5m'
                        ? 'bg-cyan-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                    }`}
                    data-testid="dot-interval-5m"
                    title="5-minute candle direction"
                  >
                    5m
                  </button>
                  <button
                    onClick={() => setDotInterval('1m')}
                    className={`px-2 py-1 text-xs font-medium transition-colors rounded-r ${
                      dotInterval === '1m'
                        ? 'bg-cyan-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                    }`}
                    data-testid="dot-interval-1m"
                    title="1-minute candle direction (MSN Finance, no rate limits)"
                  >
                    1m
                  </button>
                </div>
                {/* LIVE badge */}
                <div className="flex items-center gap-1 px-2 py-1 rounded bg-red-900/30 border border-red-700/40" data-testid="live-indicator">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                  <span className="text-xs font-medium text-red-400">LIVE</span>
                </div>
              </div>
            )}
            <button
              onClick={fetchData}
              disabled={loading}
              className="p-2.5 text-gray-400 hover:text-white"
              data-testid="refresh-intraday-patterns"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Row 2: Match interval selector */}
          {viewMode === 'chart' && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Match Interval:</span>
              <div className="flex items-center bg-gray-800 rounded border border-gray-700">
                {CORR_WINDOW_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setCorrWindow(option.intervals)}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      corrWindow === option.intervals
                        ? 'bg-cyan-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                    }`}
                    data-testid={`corr-window-${option.value}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Row 3: Best backtest time slots with CALL/PUT signals */}
          {backtestData && viewMode === 'chart' && (backtestData.top_call_slots?.length > 0 || backtestData.top_put_slots?.length > 0) && (
            <div className="flex flex-wrap items-center gap-2" data-testid="backtest-signals-row">
              <span className="text-xs text-gray-500 font-medium">Best Times:</span>
              {backtestData.best_time_slots?.slice(0, 3).map((slot, idx) => {
                const exitInfo = slot.signal === 'CALL' ? slot.best_call_exit : slot.signal === 'PUT' ? slot.best_put_exit : null;
                return (
                <div
                  key={`best-${idx}`}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium border ${
                    slot.signal === 'CALL'
                      ? 'bg-green-900/40 border-green-700/50 text-green-400'
                      : slot.signal === 'PUT'
                      ? 'bg-red-900/40 border-red-700/50 text-red-400'
                      : 'bg-gray-800 border-gray-700 text-gray-300'
                  }`}
                  title={`Entry: ${slot.time} PST — ${slot.accuracy}% accuracy, avg gain ${slot.avg_gain_pct?.toFixed(3)}%${exitInfo ? `, best exit: ${exitInfo.time} PST (+${exitInfo.avg_gain?.toFixed(3)}%)` : ''}`}
                  data-testid={`best-slot-${idx}`}
                >
                  <span className={`w-2 h-2 rounded-full ${
                    slot.signal === 'CALL' ? 'bg-green-400' : slot.signal === 'PUT' ? 'bg-red-400' : 'bg-gray-400'
                  }`}></span>
                  <span className="font-bold">{slot.time}</span>
                  <span className="opacity-75">{slot.signal}</span>
                  <span className="font-bold">{slot.accuracy}%</span>
                  {slot.avg_gain_pct > 0 && (
                    <span className="opacity-60">+{slot.avg_gain_pct?.toFixed(2)}%</span>
                  )}
                  {exitInfo && (
                    <span className="opacity-80 text-cyan-400">exit {exitInfo.time}</span>
                  )}
                </div>
                );
              })}
              {backtestData.top_call_slots?.length > 0 && (
                <>
                  <span className="text-[10px] text-gray-600">|</span>
                  <span className="text-[10px] text-green-600 font-medium">CALLS:</span>
                  {backtestData.top_call_slots.slice(0, 2).map((slot, idx) => (
                    <div
                      key={`call-${idx}`}
                      className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-green-900/30 border border-green-800/40 text-green-400"
                      title={`CALL at ${slot.time} PST — ${slot.up_accuracy}% up accuracy, avg gain +${slot.avg_gain_pct?.toFixed(3)}%${slot.best_call_exit ? `, exit: ${slot.best_call_exit.time} (+${slot.best_call_exit.avg_gain?.toFixed(3)}%)` : ''}`}
                    >
                      <span className="font-bold">{slot.time}</span>
                      <span>{slot.up_accuracy}%</span>
                      {slot.avg_gain_pct > 0 && <span className="opacity-60">+{slot.avg_gain_pct?.toFixed(2)}%</span>}
                      {slot.best_call_exit && <span className="text-cyan-400 opacity-80">exit {slot.best_call_exit.time}</span>}
                    </div>
                  ))}
                </>
              )}
              {backtestData.top_put_slots?.length > 0 && (
                <>
                  <span className="text-[10px] text-gray-600">|</span>
                  <span className="text-[10px] text-red-600 font-medium">PUTS:</span>
                  {backtestData.top_put_slots.slice(0, 2).map((slot, idx) => (
                    <div
                      key={`put-${idx}`}
                      className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-red-900/30 border border-red-800/40 text-red-400"
                      title={`PUT at ${slot.time} PST — ${slot.down_accuracy}% down accuracy, avg gain +${slot.avg_gain_pct?.toFixed(3)}%${slot.best_put_exit ? `, exit: ${slot.best_put_exit.time} (+${slot.best_put_exit.avg_gain?.toFixed(3)}%)` : ''}`}
                    >
                      <span className="font-bold">{slot.time}</span>
                      <span>{slot.down_accuracy}%</span>
                      {slot.avg_gain_pct > 0 && <span className="opacity-60">+{slot.avg_gain_pct?.toFixed(2)}%</span>}
                      {slot.best_put_exit && <span className="text-cyan-400 opacity-80">exit {slot.best_put_exit.time}</span>}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 15-Min Trade Opportunities Panel */}
      {backtestData?.best_15min_trades?.length > 0 && viewMode === 'chart' && (
        <div className="space-y-2">
          <button
            onClick={() => setShowTradePanel(!showTradePanel)}
            className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors w-full ${
              showTradePanel 
                ? 'bg-cyan-900/30 border border-cyan-700/50 text-cyan-400' 
                : 'bg-gray-800 border border-gray-700 text-gray-400 hover:text-white hover:border-gray-600'
            }`}
            data-testid="toggle-trade-panel"
          >
            <TrendingUp className="w-4 h-4" />
            <span>Best 15-Min Trades ({backtestData.best_15min_trades.length})</span>
            <span className="text-xs opacity-60">Backtested {backtestPeriod.toUpperCase()}</span>
            <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${showTradePanel ? 'rotate-180' : ''}`} />
          </button>
          
          {showTradePanel && (
            <div className="bg-gray-900/50 rounded-lg border border-gray-800 overflow-hidden" data-testid="trade-panel">
              <div className="grid grid-cols-9 gap-0 text-[11px] font-medium text-gray-500 bg-gray-800/50 px-3 py-2 border-b border-gray-700">
                <span>#</span>
                <span>Entry</span>
                <span>Exit</span>
                <span>Signal</span>
                <span>Win Rate</span>
                <span>Gain</span>
                <span>Mkt Condition</span>
                <span>Regression</span>
                <span>Samples</span>
              </div>
              {backtestData.best_15min_trades.map((trade, idx) => (
                <div
                  key={`trade-${idx}`}
                  className={`grid grid-cols-9 gap-0 text-xs px-3 py-2 border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors ${
                    idx === 0 ? 'bg-gray-800/20' : ''
                  }`}
                  data-testid={`trade-row-${idx}`}
                >
                  <span className="text-gray-600 font-mono">{idx + 1}</span>
                  <span className="font-bold text-white">
                    {trade.entry_time}
                    <span className="text-gray-600 font-normal ml-0.5">-{trade.window_end}</span>
                  </span>
                  <span className="text-cyan-400 font-bold">{trade.exit_time}</span>
                  <span>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold text-[10px] ${
                      trade.signal === 'CALL' 
                        ? 'bg-green-900/50 text-green-400 border border-green-700/40'
                        : 'bg-red-900/50 text-red-400 border border-red-700/40'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${trade.signal === 'CALL' ? 'bg-green-400' : 'bg-red-400'}`}></span>
                      {trade.signal}
                    </span>
                  </span>
                  <span className={`font-bold ${
                    trade.win_rate >= 65 ? 'text-green-400' : trade.win_rate >= 55 ? 'text-yellow-400' : 'text-gray-300'
                  }`}>
                    {trade.win_rate}%
                  </span>
                  <span className={`font-medium ${trade.total_gain_pct > 0 ? 'text-green-400' : 'text-gray-400'}`}>
                    +{trade.total_gain_pct?.toFixed(3)}%
                  </span>
                  <span className={`font-medium ${
                    trade.market_condition === 'positive' ? 'text-green-400' : 
                    trade.market_condition === 'negative' ? 'text-red-400' : 'text-yellow-400'
                  }`}>
                    {trade.positive_pct}% {trade.market_condition === 'positive' ? '+' : trade.market_condition === 'negative' ? '-' : '~'}
                  </span>
                  <span className={`font-medium ${
                    trade.regression_trend === 'up' ? 'text-green-400' : 
                    trade.regression_trend === 'down' ? 'text-red-400' : 'text-yellow-400'
                  }`}>
                    {trade.regression_trend === 'up' ? '/' : trade.regression_trend === 'down' ? '\\' : '—'} {trade.regression_up_pct}%
                  </span>
                  <span className="text-gray-500">{trade.samples}</span>
                </div>
              ))}
              <div className="px-3 py-2 text-[10px] text-gray-600 bg-gray-800/30">
                Win rates and gains based on {backtestData.pattern_window} rolling pattern backtested over {backtestData.backtest_period}. 
                Entry = 15-min window start. Exit = historically optimal close time. Not financial advice.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Description */}
      <p className="text-sm text-gray-400 px-1">
        Historical performance of each 5-minute interval starting at 6:30 AM PST (9:30 AM EST) based on {selectedDays} trading days. 
        {hasTodayData && ` Bold white line shows today's price movement. Violet line = today's running VWAP (% from open). Orange dotted = regression line (${regressionSlope >= 0 ? 'upward' : 'downward'} slope). Green/Red dashed = support/resistance zones based on today's price range.`}
        {' '}Boxes show {corrWindow * 5}-min correlation match percentages. Green = strong (≥70%), Yellow = moderate (40-70%), Gray = weak.
      </p>

      {viewMode === 'chart' ? (
        /* Chart View — explicit pixel height on ResponsiveContainer prevents
           Recharts width(-1)/height(-1) warnings on first paint when the
           parent's dimensions haven't been measured yet (Suspense boundary). */
        <div>
          <ResponsiveContainer width="100%" height={450} debounce={50}>
            <ComposedChart data={chartDataWithPredictor || finalChartData} margin={{ top: 30, right: 40, left: 10, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis 
                dataKey="time" 
                tick={{ fill: '#aaa', fontSize: 11 }}
                interval={5}
                angle={0}
                textAnchor="middle"
                height={35}
                tickFormatter={(time) => {
                  // Show only on-the-hour and half-hour marks for readability
                  if (time && time.endsWith(':00') || time && time.endsWith(':30')) return time;
                  return '';
                }}
              />
              <YAxis 
                yAxisId="left"
                tick={{ fill: '#aaa', fontSize: 12 }}
                tickFormatter={(value) => `${value.toFixed(2)}%`}
                width={65}
                domain={([dataMin, dataMax]) => {
                  // Extend Y-axis bounds to always include support/resistance levels with padding
                  const minBound = supportLevel !== null ? Math.min(dataMin, supportLevel) : dataMin;
                  const maxBound = resistanceLevel !== null ? Math.max(dataMax, resistanceLevel) : dataMax;
                  return [minBound - 0.10, maxBound + 0.10];
                }}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                tick={{ fill: '#aaa', fontSize: 12 }}
                tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
                width={55}
              />
              <YAxis 
                yAxisId="indicators"
                orientation="right"
                domain={[0, 100]}
                tick={{ fill: '#888', fontSize: 9 }}
                tickFormatter={(v) => `${v}`}
                width={0}
                hide={true}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Zero reference line */}
              <ReferenceLine yAxisId="left" y={0} stroke="#666" strokeDasharray="3 3" />
              
              {/* ENTRY ZONE — green shaded band near support (safe to enter CALL) */}
              {supportLevel !== null && resistanceLevel !== null && hasTodayData && (() => {
                const range = Math.abs(resistanceLevel - supportLevel);
                const entryTop = supportLevel + range * 0.20;  // 20% above support
                return (
                  <>
                    {/* Wide green shaded entry zone */}
                    <ReferenceArea
                      yAxisId="left"
                      y1={supportLevel - range * 0.05}
                      y2={entryTop}
                      fill="#22c55e"
                      fillOpacity={0.25}
                      stroke="#22c55e"
                      strokeOpacity={0.4}
                      strokeWidth={1}
                    />
                    {/* Inner brighter band right at support */}
                    <ReferenceArea
                      yAxisId="left"
                      y1={supportLevel - range * 0.03}
                      y2={supportLevel + range * 0.08}
                      fill="#22c55e"
                      fillOpacity={0.20}
                      stroke="none"
                    />
                    {/* Support line — solid + thicker for unmistakable visibility */}
                    <ReferenceLine 
                      yAxisId="left" 
                      y={supportLevel} 
                      stroke="#22c55e" 
                      strokeWidth={3}
                      strokeOpacity={1}
                      label={{ value: `▼ SUPPORT  ${supportLevel.toFixed(2)}%`, position: 'insideBottomLeft', fill: '#22c55e', fontSize: 11, fontWeight: 'bold' }}
                    />
                    {/* Entry zone upper boundary (dashed, lighter) */}
                    <ReferenceLine 
                      yAxisId="left" 
                      y={entryTop} 
                      stroke="#22c55e" 
                      strokeDasharray="2 4" 
                      strokeWidth={1}
                      strokeOpacity={0.6}
                    />
                  </>
                );
              })()}
              
              {/* EXIT ZONE — red shaded band near resistance (consider exit / PUT entry) */}
              {supportLevel !== null && resistanceLevel !== null && hasTodayData && (() => {
                const range = Math.abs(resistanceLevel - supportLevel);
                const exitBottom = resistanceLevel - range * 0.20;  // 20% below resistance
                return (
                  <>
                    {/* Wide red shaded exit zone */}
                    <ReferenceArea
                      yAxisId="left"
                      y1={exitBottom}
                      y2={resistanceLevel + range * 0.05}
                      fill="#ef4444"
                      fillOpacity={0.25}
                      stroke="#ef4444"
                      strokeOpacity={0.4}
                      strokeWidth={1}
                    />
                    {/* Inner brighter band right at resistance */}
                    <ReferenceArea
                      yAxisId="left"
                      y1={resistanceLevel - range * 0.08}
                      y2={resistanceLevel + range * 0.03}
                      fill="#ef4444"
                      fillOpacity={0.20}
                      stroke="none"
                    />
                    {/* Resistance line — solid + thicker for unmistakable visibility */}
                    <ReferenceLine 
                      yAxisId="left" 
                      y={resistanceLevel} 
                      stroke="#ef4444" 
                      strokeWidth={3}
                      strokeOpacity={1}
                      label={{ value: `▲ RESISTANCE  ${resistanceLevel.toFixed(2)}%`, position: 'insideTopLeft', fill: '#ef4444', fontSize: 11, fontWeight: 'bold' }}
                    />
                    {/* Exit zone lower boundary (dashed, lighter) */}
                    <ReferenceLine 
                      yAxisId="left" 
                      y={exitBottom} 
                      stroke="#ef4444" 
                      strokeDasharray="2 4" 
                      strokeWidth={1}
                      strokeOpacity={0.6}
                    />
                  </>
                );
              })()}
              
              {/* 30-Minute Correlation Windows — labels only, no background fill */}
              {correlations30Min.map((window, idx) => (
                <ReferenceArea
                  key={`corr-window-${idx}`}
                  yAxisId="left"
                  x1={window.startTime}
                  x2={window.endTime}
                  fill="transparent"
                  fillOpacity={0}
                  stroke="none"
                  label={window.strength !== 'weak' ? {
                    value: `${window.percentage}%`,
                    position: 'top',
                    fill: window.strength === 'strong' ? '#22c55e' : '#eab308',
                    fontSize: 11,
                    fontWeight: 'bold'
                  } : undefined}
                />
              ))}
              
              {/* Volume area (background) */}
              <Area
                yAxisId="right"
                type="monotone"
                dataKey="avgVolume"
                fill="#3b82f6"
                fillOpacity={0.15}
                stroke="none"
              />
              
              {/* Average change bars */}
              <Bar yAxisId="left" dataKey="avgChange" radius={[2, 2, 0, 0]}>
                {finalChartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.avgChange >= 0 ? '#22c55e' : '#ef4444'}
                    fillOpacity={0.8}
                  />
                ))}
              </Bar>
              
              {/* Up percentage trend line - ENHANCED VISIBILITY */}
              {/* This shows the historical Up % at each interval, centered around 50% */}
              <Line
                yAxisId="left"
                type="monotone"
                dataKey={(d) => (d.upPct - 50) / 100}
                stroke="#fbbf24"
                strokeWidth={2}
                dot={false}
                strokeOpacity={0.9}
                name="Up % Trend"
              />
              
              {/* Today's price movement line - BOLD WHITE with live pulsing dot */}
              {hasTodayData && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="todayChange"
                  stroke="#ffffff"
                  strokeWidth={3}
                  dot={renderLiveDot}
                  activeDot={{ r: 5, fill: '#ffffff', stroke: '#000' }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )}

              {/* Today's running VWAP — VIOLET line, % from open */}
              {hasTodayData && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="todayVwap"
                  stroke="#ee82ee"
                  strokeWidth={2.5}
                  dot={false}
                  connectNulls={true}
                  isAnimationActive={false}
                  name="Today's VWAP (% from open)"
                />
              )}
              
              {/* Regression line on today's white line — orange dotted, thick and visible */}
              {hasTodayData && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="regressionValue"
                  stroke="#f97316"
                  strokeWidth={3}
                  strokeDasharray="5 5"
                  dot={false}
                  connectNulls={true}
                  isAnimationActive={false}
                  name="Regression"
                />
              )}

              {/* 15-MIN PREDICTOR — cyan dashed line projecting forward from
                  the last white-line point to end of session, using the slope
                  of the most recent 15 minutes of live price action. */}
              {hasTodayData && predictor15Slope !== null && (
                <Line
                  yAxisId="left"
                  type="linear"
                  dataKey="predictor15Value"
                  stroke="#22d3ee"
                  strokeWidth={2.5}
                  strokeDasharray="6 4"
                  strokeOpacity={0.95}
                  dot={false}
                  connectNulls={true}
                  isAnimationActive={false}
                  name="15m Predictor"
                />
              )}

              {/* Floating Est. Close $ price label anchored at 1 PM PST —
                  shows the dollar price at the end of the 15m predictor line
                  plus the 5Y-backtested confidence %. */}
              {hasTodayData && predictor15EndValue !== null && todayOpen && todayOpen > 0 && finalChartData && finalChartData.length > 0 && (
                <ReferenceDot
                  yAxisId="left"
                  x={finalChartData[finalChartData.length - 1].time}
                  y={predictor15EndValue}
                  r={4}
                  fill="#22d3ee"
                  stroke="#0e7490"
                  strokeWidth={1.5}
                  isFront={true}
                  label={{
                    value: (() => {
                      const price = todayOpen * (1 + predictor15EndValue / 100);
                      const conf = predictorAccuracy?.confidence_pct;
                      const band = predictorAccuracy?.confidence_band_pct ?? 1;
                      if (conf !== undefined && conf !== null) {
                        return `Est. Close: $${price.toFixed(2)} • ${conf}% conf (±${band}%)`;
                      }
                      return `Est. Close: $${price.toFixed(2)}`;
                    })(),
                    position: 'left',
                    offset: 8,
                    fill: '#67e8f9',
                    fontSize: 11,
                    fontWeight: 'bold',
                  }}
                />
              )}
              
              {/* RSI (14-period) — computed from today's 5-min candles */}
              {hasTodayData && (
                <Line
                  yAxisId="indicators"
                  type="monotone"
                  dataKey="rsi"
                  stroke="#ef4444"
                  strokeWidth={4}
                  dot={false}
                  connectNulls={true}
                  isAnimationActive={false}
                  name="RSI"
                  strokeOpacity={0.95}
                />
              )}
              
              {/* MFI (14-period) — computed from today's 5-min OHLCV candles */}
              {hasTodayData && (
                <Line
                  yAxisId="indicators"
                  type="monotone"
                  dataKey="mfi"
                  stroke="#22c55e"
                  strokeWidth={4}
                  dot={false}
                  connectNulls={true}
                  isAnimationActive={false}
                  name="MFI"
                  strokeOpacity={0.95}
                />
              )}
              
              {/* RSI/MFI reference levels: overbought (70) and oversold (30) */}
              {hasTodayData && (
                <>
                  <ReferenceLine yAxisId="indicators" y={70} stroke="#ef4444" strokeDasharray="3 3" strokeOpacity={0.4} />
                  <ReferenceLine yAxisId="indicators" y={30} stroke="#22c55e" strokeDasharray="3 3" strokeOpacity={0.4} />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        /* Table View */
        <div className="max-h-[400px] overflow-auto border border-gray-800 rounded">
          <table className="w-full text-xs">
            <thead className="bg-gray-800 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-gray-400 font-medium">Time (PST)</th>
                <th className="px-3 py-2 text-left text-gray-400 font-medium">Time (EST)</th>
                <th className="px-3 py-2 text-right text-gray-400 font-medium">Avg Change %</th>
                {hasTodayData && (
                  <th className="px-3 py-2 text-right text-white font-medium">Today %</th>
                )}
                <th className="px-3 py-2 text-right text-gray-400 font-medium">Avg Volume</th>
                <th className="px-3 py-2 text-right text-gray-400 font-medium">Up %</th>
                <th className="px-3 py-2 text-right text-gray-400 font-medium">Down %</th>
                <th className="px-3 py-2 text-center text-gray-400 font-medium">Periods</th>
                <th className="px-3 py-2 text-center text-gray-400 font-medium">Signal</th>
              </tr>
            </thead>
            <tbody>
              {chartData.map((pattern, idx) => (
                <tr 
                  key={idx} 
                  className={`border-b border-gray-800 hover:bg-gray-800/50 ${
                    pattern.avgChange > 0.01 ? 'bg-green-900/10' :
                    pattern.avgChange < -0.01 ? 'bg-red-900/10' : ''
                  }`}
                >
                  <td className="px-3 py-2 text-white font-mono">{pattern.time}</td>
                  <td className="px-3 py-2 text-gray-400 font-mono">{pattern.timeEst}</td>
                  <td className={`px-3 py-2 text-right font-mono ${
                    pattern.avgChange >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {pattern.avgChange >= 0 ? '+' : ''}{pattern.avgChange.toFixed(4)}%
                  </td>
                  {hasTodayData && (
                    <td className={`px-3 py-2 text-right font-mono font-bold ${
                      pattern.todayChange === null ? 'text-gray-600' :
                      pattern.todayChange >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {pattern.todayChange !== null 
                        ? `${pattern.todayChange >= 0 ? '+' : ''}${pattern.todayChange.toFixed(4)}%`
                        : '-'
                      }
                    </td>
                  )}
                  <td className="px-3 py-2 text-right text-blue-400 font-mono">
                    {pattern.avgVolume.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right text-green-400">
                    {pattern.upPct.toFixed(2)}%
                  </td>
                  <td className="px-3 py-2 text-right text-red-400">
                    {pattern.downPct.toFixed(2)}%
                  </td>
                  <td className="px-3 py-2 text-center text-gray-400">
                    {pattern.periods}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                      pattern.signal === 'BUY' ? 'bg-green-900/50 text-green-400' :
                      pattern.signal === 'SELL' ? 'bg-red-900/50 text-red-400' :
                      'bg-gray-700 text-gray-300'
                    }`}>
                      {getSignalIcon(pattern.signal)}
                      {pattern.signal}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[9px] text-gray-500 p-2 bg-gray-900">
            * Scroll to view all {totalIntervals} 5-minute intervals • Green rows indicate positive average gains, red rows indicate losses
          </div>
        </div>
      )}

      {/* Today's range + 52-week range sliders — above predictor info bar */}
      {priceRange && priceRange.current != null && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-3 py-3 rounded-md border border-gray-800 bg-gray-900/40" data-testid="price-range-bars">
          <PriceRangeBar
            label="Today's range"
            low={priceRange.dayLow}
            high={priceRange.dayHigh}
            current={priceRange.current}
            testId="range-bar-today"
          />
          <PriceRangeBar
            label="52 week range"
            low={priceRange.wkLow}
            high={priceRange.wkHigh}
            current={priceRange.current}
            testId="range-bar-52w"
          />
        </div>
      )}

      {/* 15-Min Predictor Info Bar — placed directly under the chart, above the legend */}
      {hasTodayData && predictor15Slope !== null && (
        <div
          data-testid="predictor-info-bar"
          className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 rounded-md border border-cyan-900/40 bg-gradient-to-r from-cyan-950/30 via-gray-900/40 to-gray-900/40"
        >
          <div className="flex items-center gap-2 text-[12px]">
            <span className="w-6 h-0.5 rounded" style={{ borderTop: '2.5px dashed #22d3ee' }}></span>
            <span className="text-cyan-300 font-semibold tracking-wide">15-MIN PREDICTOR</span>
            <span
              className={`px-1.5 py-0.5 rounded font-mono text-[11px] ${
                predictor15Slope > 0.002
                  ? 'bg-green-900/50 text-green-300'
                  : predictor15Slope < -0.002
                  ? 'bg-red-900/50 text-red-300'
                  : 'bg-gray-800 text-gray-300'
              }`}
              data-testid="predictor-slope-badge"
            >
              {predictor15Slope > 0.002 ? 'UP' : predictor15Slope < -0.002 ? 'DOWN' : 'FLAT'}
              {' '}• slope {predictor15Slope >= 0 ? '+' : ''}{(predictor15Slope * 100).toFixed(3)}%/5m
            </span>
            {predictor15EndValue !== null && (
              <span className="text-gray-400">
                → EOD target:{' '}
                <span className={`font-mono ${predictor15EndValue >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {predictor15EndValue >= 0 ? '+' : ''}{predictor15EndValue.toFixed(3)}%
                </span>
              </span>
            )}
            {predictor15EndValue !== null && todayOpen && todayOpen > 0 && (
              <span
                className="ml-1 px-2 py-0.5 rounded border border-green-800/60 bg-green-950/40 text-[12px] flex items-center gap-1"
                data-testid="predictor-est-close"
              >
                <span className="text-gray-400 uppercase tracking-wide text-[10px]">Est. Close:</span>
                <span className={`font-mono font-bold ${predictor15EndValue >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                  ${(todayOpen * (1 + predictor15EndValue / 100)).toFixed(2)}
                </span>
                <span className={`font-mono text-[10px] ${predictor15EndValue >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  ({predictor15EndValue >= 0 ? '+' : ''}{predictor15EndValue.toFixed(2)}%)
                </span>
                {predictorAccuracy?.confidence_pct !== undefined && predictorAccuracy?.confidence_pct !== null && (
                  <span
                    className={`ml-1 px-1.5 py-0.5 rounded font-mono text-[10px] font-bold ${
                      predictorAccuracy.confidence_pct >= 80
                        ? 'bg-green-800/60 text-green-200'
                        : predictorAccuracy.confidence_pct >= 60
                        ? 'bg-yellow-800/60 text-yellow-200'
                        : 'bg-red-800/60 text-red-200'
                    }`}
                    data-testid="predictor-confidence-badge"
                    title={`${predictorAccuracy.confidence_pct}% of backtested predictions landed within ±${predictorAccuracy.confidence_band_pct}% of projected close`}
                  >
                    {predictorAccuracy.confidence_pct}% conf (±{predictorAccuracy.confidence_band_pct}%)
                  </span>
                )}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 text-[11px]" data-testid="predictor-accuracy-stats">
            <span className="text-gray-500 uppercase tracking-wide">5Y Backtest:</span>
            {predictorLoading && !predictorAccuracy ? (
              <span className="text-gray-500 italic">computing…</span>
            ) : predictorAccuracy ? (
              <>
                <span className="flex items-center gap-1">
                  <span className="text-gray-400">Direction</span>
                  <span
                    className={`font-mono font-bold ${
                      predictorAccuracy.direction_accuracy_pct >= 55
                        ? 'text-green-400'
                        : predictorAccuracy.direction_accuracy_pct >= 50
                        ? 'text-yellow-400'
                        : 'text-red-400'
                    }`}
                    data-testid="predictor-direction-accuracy"
                  >
                    {predictorAccuracy.direction_accuracy_pct}%
                  </span>
                </span>
                <span className="text-gray-700">|</span>
                <span className="flex items-center gap-1">
                  <span className="text-gray-400">±{predictorAccuracy.tolerance_pct}% band</span>
                  <span
                    className={`font-mono font-bold ${
                      predictorAccuracy.tolerance_accuracy_pct >= 45
                        ? 'text-green-400'
                        : predictorAccuracy.tolerance_accuracy_pct >= 30
                        ? 'text-yellow-400'
                        : 'text-red-400'
                    }`}
                    data-testid="predictor-tolerance-accuracy"
                  >
                    {predictorAccuracy.tolerance_accuracy_pct}%
                  </span>
                </span>
                <span className="text-gray-700">|</span>
                <span className="text-gray-500 font-mono" data-testid="predictor-sample-count">
                  {predictorAccuracy.total_samples.toLocaleString()} samples
                </span>
              </>
            ) : (
              <span className="text-gray-600 italic">unavailable</span>
            )}
          </div>
        </div>
      )}

      {/* RSI + MFI + CANDLE ANALYSIS PANEL (5-year backtested) */}
      {hasTodayData && (currentRsi != null || currentMfi != null) && (
        <div
          data-testid="rsi-mfi-analysis-panel"
          className="rounded-md border border-indigo-900/40 bg-gradient-to-br from-indigo-950/30 via-gray-900/50 to-gray-900/40 p-3 space-y-2"
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-cyan-300 text-xs font-semibold tracking-wide uppercase">
                Momentum & Money Flow Analysis
              </span>
              <button
                onClick={toggleAlerts}
                data-testid="strength-alerts-toggle"
                title={alertsEnabled
                  ? 'Alerts ON — toast + beep when strength flips to EXTREME. Click to disable.'
                  : 'Alerts OFF — click to enable toast + beep when strength flips to EXTREME.'}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold transition-colors ${
                  alertsEnabled
                    ? 'bg-amber-900/50 border-amber-700 text-amber-200 hover:bg-amber-800/60'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {alertsEnabled ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
                <span>{alertsEnabled ? 'Alerts ON' : 'Alerts OFF'}</span>
              </button>
            </div>
            {rsiMfiAnalysis && (
              <span
                className={`px-2 py-0.5 rounded font-mono text-[11px] font-bold ${
                  rsiMfiAnalysis.direction === 'BULLISH' ? 'bg-green-800/60 text-green-200'
                  : rsiMfiAnalysis.direction === 'BEARISH' ? 'bg-red-800/60 text-red-200'
                  : 'bg-gray-800 text-gray-300'
                }`}
                data-testid="rsi-mfi-verdict"
              >
                {rsiMfiAnalysis.direction} • {rsiMfiAnalysis.confidence_pct}% conf
              </span>
            )}
            {!rsiMfiAnalysis && rsiMfiLoading && (
              <span className="text-[11px] italic text-gray-500">computing 5Y backtest…</span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
            {/* RSI BLOCK */}
            <div className="bg-gray-900/60 rounded p-2 border border-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-red-400 font-semibold">RSI</span>
                <span className="font-mono text-white font-bold">{currentRsi != null ? currentRsi.toFixed(1) : '—'}</span>
              </div>
              <p className="text-gray-400 mt-1 leading-snug">
                {rsiMfiAnalysis?.rsi_interpretation || 'Waiting for analysis…'}
              </p>
            </div>

            {/* MFI BLOCK */}
            <div className="bg-gray-900/60 rounded p-2 border border-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-green-400 font-semibold">MFI</span>
                <span className="font-mono text-white font-bold">{currentMfi != null ? currentMfi.toFixed(1) : '—'}</span>
              </div>
              <p className="text-gray-400 mt-1 leading-snug">
                {rsiMfiAnalysis?.mfi_interpretation || 'Waiting for analysis…'}
              </p>
            </div>

            {/* CANDLE + STRENGTH */}
            <div className="bg-gray-900/60 rounded p-2 border border-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-amber-400 font-semibold">Candle / Strength</span>
                <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${
                  currentCandleDirection === 'bullish' ? 'bg-green-900/60 text-green-300'
                  : currentCandleDirection === 'bearish' ? 'bg-red-900/60 text-red-300'
                  : 'bg-gray-800 text-gray-300'
                }`}>
                  {currentCandleDirection.toUpperCase()}
                </span>
              </div>
              <p className="text-gray-400 mt-1 leading-snug">
                {rsiMfiAnalysis?.candle_signal || 'Candle forming…'}
                {rsiMfiAnalysis?.strength && (
                  <> <span className="text-gray-300 font-semibold"> • Strength: {rsiMfiAnalysis.strength}</span></>
                )}
              </p>
            </div>
          </div>

          {/* DIVERGENCE ALERTS */}
          {divergenceInfo?.results?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="divergence-alerts">
              <span className="text-[10px] uppercase tracking-wide text-purple-300 font-semibold">Divergence:</span>
              {divergenceInfo.results.map((r, idx) => (
                <span
                  key={idx}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
                    r.type === 'BULLISH_DIVERGENCE' ? 'bg-green-900/60 text-green-200 border border-green-700/40'
                    : 'bg-red-900/60 text-red-200 border border-red-700/40'
                  }`}
                  title={r.desc}
                >
                  {r.type === 'BULLISH_DIVERGENCE' ? '↑' : '↓'} {r.indicator} {r.type === 'BULLISH_DIVERGENCE' ? 'BULLISH DIV' : 'BEARISH DIV'}
                </span>
              ))}
            </div>
          )}

          {/* 5Y BACKTEST STATS */}
          {rsiMfiAnalysis && rsiMfiAnalysis.sample_count > 0 && (
            <div className="flex flex-wrap items-center gap-3 pt-1 text-[11px] text-gray-400 border-t border-gray-800/60">
              <span className="text-[10px] uppercase tracking-wide text-gray-500">5Y Backtest (next {rsiMfiAnalysis.horizon_bars} bars):</span>
              <span>
                Up <span className="font-mono font-bold text-green-400">{rsiMfiAnalysis.historical_up_pct}%</span>
              </span>
              <span className="text-gray-700">|</span>
              <span>
                Down <span className="font-mono font-bold text-red-400">{rsiMfiAnalysis.historical_down_pct}%</span>
              </span>
              <span className="text-gray-700">|</span>
              <span>
                Avg Move{' '}
                <span className={`font-mono ${rsiMfiAnalysis.avg_move_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {rsiMfiAnalysis.avg_move_pct >= 0 ? '+' : ''}{rsiMfiAnalysis.avg_move_pct}%
                </span>
              </span>
              <span className="text-gray-700">|</span>
              <span className="font-mono">{rsiMfiAnalysis.sample_count.toLocaleString()} samples</span>
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 text-sm text-gray-400 flex-wrap py-2">
        <span className="flex items-center gap-2">
          <span className="w-4 h-4 bg-green-500/80 rounded"></span> Positive Avg
        </span>
        <span className="flex items-center gap-2">
          <span className="w-4 h-4 bg-red-500/80 rounded"></span> Negative Avg
        </span>
        {hasTodayData && (
          <span className="flex items-center gap-2">
            <span className="w-6 h-1 bg-white rounded"></span> Today
          </span>
        )}
        <span className="flex items-center gap-2">
          <span className="w-6 h-1 bg-amber-500 rounded"></span> Up % Trend
        </span>
        <span className="flex items-center gap-2">
          <span className="w-6 h-0.5 rounded" style={{borderTop: '3px dotted #f97316'}}></span> Regression
        </span>
        <span className="flex items-center gap-2">
          <span className="w-6 h-0.5 rounded" style={{borderTop: '2.5px dashed #22d3ee'}}></span> 15m Predictor
        </span>
        <span className="flex items-center gap-2">
          <span className="w-6 h-0.5 bg-red-500 rounded"></span> RSI
        </span>
        <span className="flex items-center gap-2">
          <span className="w-6 h-0.5 bg-green-500 rounded"></span> MFI
        </span>
        <span className="flex items-center gap-2">
          <span className="w-6 h-0.5 rounded" style={{borderTop: '2px dashed #22c55e'}}></span> Support
        </span>
        <span className="flex items-center gap-2">
          <span className="w-4 h-3 bg-green-500/25 border border-green-500/40 rounded"></span> Entry Zone
        </span>
        <span className="flex items-center gap-2">
          <span className="w-6 h-0.5 rounded" style={{borderTop: '2px dashed #ef4444'}}></span> Resistance
        </span>
        <span className="flex items-center gap-2">
          <span className="w-4 h-3 bg-red-500/25 border border-red-500/40 rounded"></span> Exit Zone
        </span>
        <span className="flex items-center gap-2">
          <span className="w-4 h-4 border-2 border-green-500 bg-green-500/20 rounded"></span> Strong (≥70%)
        </span>
        <span className="flex items-center gap-2">
          <span className="w-4 h-4 border-2 border-yellow-500 bg-yellow-500/20 rounded"></span> Moderate (40-70%)
        </span>
        <span className="flex items-center gap-2">
          <span className="w-4 h-4 border-2 border-gray-500 bg-gray-500/20 rounded"></span> Weak (&lt;40%)
        </span>
      </div>
    </div>
  );
}
